import { format } from 'date-fns'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { SecurityEnum } from 'features/declaration/models/schemas/declarationEnums'
import { CustomsOfficeOfExitForTransitDeclared, CustomsOfficeOfTransitDeclared } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { formatToIsoDateTimeString, formatToLocalDateTimeString } from 'shared/lib/datetime'
import {
  CreateOrUpdateTransitOperationRequest,
  CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum,
  CreateOrUpdateTransitOperationRequestDeclarationTypeEnum,
  CreateOrUpdateCustomsOfficeOfTransitRequest,
  CustomsOfficeOfTransitResponse,
  TransitOperationResponse,
} from '../../../../api/models'
import { CreateOrUpdateCustomsOfficeOfTransitExitRequest } from '../../../../api/models/create-or-update-customs-office-of-transit-exit-request'
import { CustomsOfficeOfTransitExitResponse } from '../../../../api/models/customs-office-of-transit-exit-response'
import { GuaranteeCalculation, RestrictionCheck, TransitOperation } from './types'
import { NctsError, NctsPhase, NctsScope } from '../../../../shared/ncts/types'
import { FieldRequirement } from '../../models/fieldRules'
import { DeclarationValidationResponse } from '../../../../api/models/declaration-validation-response'
import type { DeclarationFieldErrorResponse } from '../../../../api/models/declaration-field-error-response'
import { DeclarationValidationRequest, DeclarationValidationRequestPhaseIdEnum } from '../../../../api/models/declaration-validation-request'
import { DeclarationFieldErrorResponseRequirement } from '../../../../api/models/declaration-field-error-response-requirement'
import { RuleConditionsResponse } from '../../../../api/models/rule-conditions-response'
import { RuleConditions, ServerValidationResult } from '../../models/types'
import { GuaranteeCalculationResponse } from '../../../../api/models/guarantee-calculation-response'
import { TransitOperationRestrictionCheckResponse } from '../../../../api/models/transit-operation-restriction-check-response'
import { TransitOperationSave } from './request'

export function toCreateOrUpdateCustomsOfficeOfTransitRequest(
  item: CustomsOfficeOfTransitDeclared,
  index: number,
): CreateOrUpdateCustomsOfficeOfTransitRequest {
  return {
    id: item.id ?? undefined,
    sequenceNumber: index,
    referenceNumber: item.referenceNumber,
    arrivalDateAndTime: formatToIsoDateTimeString(item.arrivalDateAndTime),
    entryIndicator: item.entryIndicator === 'true',
    reEntryIndicator: item.reEntryIndicator === 'true',
  }
}

export function toCreateOrUpdateCustomsOfficeOfExitForTransitRequest(
  item: CustomsOfficeOfExitForTransitDeclared,
  index: number,
): CreateOrUpdateCustomsOfficeOfTransitExitRequest {
  return {
    id: item.id ?? undefined,
    sequenceNumber: index,
    referenceNumber: item.referenceNumber,
  }
}

export function parseCustomsOfficeOfTransitResponse(office: CustomsOfficeOfTransitResponse, index: number): CustomsOfficeOfTransitDeclared {
  return {
    id: office.id,
    deleted: false,
    sequenceNumber: office.sequenceNumber ?? index,
    arrivalDateAndTime: formatToLocalDateTimeString(office.arrivalDateAndTime),
    referenceNumber: office.referenceNumber ?? '',
    entryIndicator: office?.entryIndicator === true ? 'true' : 'false',
    reEntryIndicator: office?.reEntryIndicator === true ? 'true' : 'false',
  }
}

export function parseCustomsOfficeOfTransitExitResponse(
  office: CustomsOfficeOfTransitExitResponse,
  index: number,
): CustomsOfficeOfExitForTransitDeclared {
  return {
    id: office.id,
    deleted: false,
    sequenceNumber: office.sequenceNumber ?? index,
    referenceNumber: office.referenceNumber ?? '',
  }
}

const FIELD_REQUIREMENT_MAP: Record<DeclarationFieldErrorResponseRequirement, FieldRequirement> = {
  [DeclarationFieldErrorResponseRequirement.Required]: 'required',
  [DeclarationFieldErrorResponseRequirement.Optional]: 'optional',
  [DeclarationFieldErrorResponseRequirement.Forbidden]: 'forbidden',
}

export function fromTransitOperationResponse(
  response: TransitOperationResponse | undefined,
): TransitOperation {
  const declarationType: string = response?.declarationType?.toString() ?? 'T1'
  const additionalDeclarationType: string = response?.additionalDeclarationType?.toString() ?? 'A'

  return {
    id: response?.id ?? null,
    declarationType: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[
      declarationType as keyof typeof CreateOrUpdateTransitOperationRequestDeclarationTypeEnum],
    additionalDeclarationType: CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[
      additionalDeclarationType as keyof typeof CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum],
    security: SecurityEnum.parse(response?.security?.charAt(1) ?? '0'),
    specificCircumstanceIndicator: response?.specificCircumstanceIndicator ?? '',
    transitCustomsOffices: response?.customsOfficeOfTransits?.map(parseCustomsOfficeOfTransitResponse) ?? [],
    customsOfficesOfTransitExit: response?.customsOfficesOfTransitExit?.map(parseCustomsOfficeOfTransitExitResponse) ?? [],
    departureCustomsOffice: response?.customsOfficeOfDeparture ?? '',
    destinationCustomsOffice: response?.customsOfficeOfDestination ?? '',
    bindingItinerary: response?.bindingItinerary === true ? 'true' : 'false',
    entryIndicator: response?.entryIndicator === true ? 'true' : 'false',
    customerId: response?.customerId ?? null,
    limitDate: response?.limitDate ? format(new Date(response.limitDate), 'yyyy-MM-dd') : '',
    lrn: response?.lrn ?? '',
    mrn: response?.mrn ?? '',
    ensMrn: response?.ensMrn ?? '',
    status: response?.status ?? 'DRAFT',
    directoId: response?.directoId ?? null,
    auditReason: response?.auditReason ?? '',
    feedback: response?.feedback ?? '',
    transitOperationInfoId: response?.transitOperationInfoId,
    guaranteeClaimReservationStatus: response?.guaranteeClaimReservationStatus ?? '',
  }
}

export function toTransitOperationSave(formValues: TransitOperationFormValues): TransitOperationSave {
  return {
    departureCustomsOffice: formValues.departureCustomsOffice,
    destinationCustomsOffice: formValues.destinationCustomsOffice,
    declarationType: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[formValues.declarationType],
    security: formValues.security,
    specificCircumstanceIndicator: formValues.specificCircumstanceIndicator,
    additionalDeclarationType:
      CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[formValues.additionalDeclarationType],
    bindingItinerary: formValues.bindingItinerary,
    entryIndicator: formValues.entryIndicator,
    transitCustomsOffices: formValues.transitCustomsOffices
      .filter((item) => !item.deleted),
    customsOfficesOfTransitExit: formValues.customsOfficesOfTransitExit
      .filter((item) => !item.deleted),
    limitDate: formValues.limitDate ?? '',
  }
}

export function toUpdateRequest(data: TransitOperationSave): CreateOrUpdateTransitOperationRequest {
  return {
    customsOfficeOfDeparture: data.departureCustomsOffice,
    customsOfficeOfDestination: data.destinationCustomsOffice,
    declarationType: CreateOrUpdateTransitOperationRequestDeclarationTypeEnum[data.declarationType],
    security: data.security,
    specificCircumstanceIndicator: data.specificCircumstanceIndicator,
    additionalDeclarationType:
      CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum[data.additionalDeclarationType],
    reducedDatasetIndicator: false,
    bindingItinerary: data.bindingItinerary === 'true',
    entryIndicator: data.entryIndicator === 'true',
    customsOfficeOfTransits: data.transitCustomsOffices
      .filter((item) => !item.deleted)
      .map(toCreateOrUpdateCustomsOfficeOfTransitRequest),
    customsOfficesOfTransitExit: data.customsOfficesOfTransitExit
      .filter((item) => !item.deleted)
      .map(toCreateOrUpdateCustomsOfficeOfExitForTransitRequest),
    limitDate: data.limitDate ?? '',
  }
}

export function toValidateRequest({
  phase,
  houseConsignmentId,
  consignmentItemId,
  isOptIn,
}: {
  phase: NctsPhase
  houseConsignmentId?: number
  consignmentItemId?: number
  isOptIn?: boolean
}): DeclarationValidationRequest {
  return {
    phaseId: phase === 'p5' ? DeclarationValidationRequestPhaseIdEnum.P5 : DeclarationValidationRequestPhaseIdEnum.P6,
    houseConsignmentId,
    consignmentItemId,
    isOptIn,
  }
}

export function fromNctsErrorResponse(response: DeclarationFieldErrorResponse, scope?: NctsScope): NctsError {
  return {
    path: response.path ?? '',
    message: response.message ?? '',
    code: response.code,
    scope,
  }
}

export function fromRuleConditionsResponse(response: RuleConditionsResponse): RuleConditions {
  return {
    hasItemWithCtcDestination: !!response.hasItemWithCtcDestination,
    hasItemsWithCountryOfDispatch: !!response.hasItemsWithCountryOfDispatch,
    hasItemsWithCountryOfDestination: !!response.hasItemsWithCountryOfDestination,
    hasItemsWithUcr: !!response.hasItemsWithUcr,
    isConsignmentConsignorPresent: !!response.isConsignmentConsignorPresent,
    isConsignmentConsigneePresent: !!response.isConsignmentConsigneePresent,
    hasHouseConsignmentWithCtcDestination: !!response.hasHouseConsignmentWithCtcDestination,
    hasConsignmentAdditionalReference30600: !!response.hasConsignmentAdditionalReference30600,
    hasHouseConsignmentAdditionalReference30600: !!response.hasHouseConsignmentAdditionalReference30600,
    consignmentCountryOfDestination: response.consignmentCountryOfDestination ?? '',
    consignmentPlaceOfAcceptanceCountry: response.consignmentPlaceOfAcceptanceCountry ?? '',
    consignmentPlaceOfDeliveryCountry: response.consignmentPlaceOfDeliveryCountry ?? '',
  }
}

export function fromValidationResponse(response: DeclarationValidationResponse, scope?: NctsScope): ServerValidationResult {
  return {
    fieldRequirements: Object.fromEntries(
      Object.entries(response.fieldRequirements ?? {})
        .map(([k, v]) => [k, FIELD_REQUIREMENT_MAP[v]]),
    ),
    errors: (response.errors ?? [])
      .map((errorResponse) => fromNctsErrorResponse(errorResponse, scope)),
    ruleConditions: fromRuleConditionsResponse(response.ruleConditions ?? {}),
  }
}

export function fromGuaranteeCalculationResponse(response: GuaranteeCalculationResponse): GuaranteeCalculation {
  return {
    lrn: response.lrn ?? '',
    mrn: response.mrn ?? '',
    createdAt: response.createdAt ?? null,
    foreignCurrency: response.foreignCurrency ?? '',
    currencyExchangeRate: response.currencyExchangeRate ?? 0,
    calculatedExciseSum: response.calculatedExciseSum ?? 0,
    calculatedDutySum: response.calculatedDutySum ?? 0,
    calculatedVatSum: response.calculatedVatSum ?? 0,
    calculatedTaxesSum: response.calculatedTaxesSum ?? 0,
    foreignCurrencyCalculatedSum: response.foreignCurrencyCalculatedSum ?? 0,
    rows: (response.rows ?? []).map((r, index) => ({
      sequenceNumber: r.sequenceNumber ?? index,
      description: r.description ?? '',
      commodityCode: r.commodityCode ?? '',
      price: r.price ?? 0,
      excise: r.excise ?? 0,
      dutyRate: r.dutyRate ?? 0,
      vatRate: r.vatRate ?? 0,
      calculatedGuaranteedAmount: r.calculatedGuaranteedAmount ?? 0,
      calculatedDuty: r.calculatedDuty ?? 0,
      calculatedVat: r.calculatedVat ?? 0,
    })),
  }
}

export function fromRestrictionCheckResponse(response: TransitOperationRestrictionCheckResponse): RestrictionCheck {
  return {
    restrictedConsignmentItems: (response.restrictedConsignmentItems ?? []).map((item) => ({
      consignmentItemId: item.consignmentItemId ?? null,
      declarationGoodsItemNumber: item.declarationGoodsItemNumber ?? null,
      hsCode: item.hsCode ?? null,
      descriptionOfGoods: item.descriptionOfGoods ?? null,
    })),
    restrictedTraderNames: response.restrictedTraderNames ?? [],
    restrictedTransportMeans: response.restrictedTransportMeans ?? [],
  }
}
